import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../app/AuthContext";
import { ReleaseReportPanel } from "./ReleaseReportPanel";
import { api } from "../../shared/api/client";
import type {
  PageResponse,
  ReleaseSlotBookingView,
  ReleaseSummary,
} from "../../shared/types/api";
import {
  Button,
  DataTable,
  Field,
  Input,
  ProgressBar,
  Select,
  StatusBadge,
  type Column,
} from "../../shared/ui";
import { ReleaseFormDrawer } from "./ReleaseFormDrawer";
import {
  READINESS_LABELS,
  READINESS_TONE,
  RELEASE_STATUS_LABELS,
  RELEASE_STATUS_TONE,
  RELEASE_TYPE_LABELS,
  RELEASE_TYPE_TONE,
} from "./releaseVocabulary";

/**
 * §9's release list, as a tab inside the project.
 *
 * <p>The tabs §9 asks for are filters over one query rather than separate screens, because they
 * are the same list asked a narrower question — and a tab that fetched its own endpoint would go
 * stale differently from its neighbours.
 *
 * <p>Every figure here comes from the stored counters on the release row, not from evaluating
 * gates per row (§84). Fifty releases would otherwise mean five hundred gate evaluations to draw
 * a table whose job is only to say which ones are worth opening.
 */
interface ReleaseTab {
  key: string;
  label: string;
  statuses?: string[];
  archived?: boolean;
}

const ALL_RELEASES: ReleaseTab = { key: "all", label: "All" };

const TABS: ReleaseTab[] = [
  ALL_RELEASES,
  { key: "upcoming", label: "Upcoming", statuses: ["DRAFT", "PLANNING", "SCOPE_FROZEN", "VALIDATING"] },
  { key: "ready", label: "Ready", statuses: ["READY", "APPROVED", "SCHEDULED"] },
  { key: "in-progress", label: "In progress", statuses: ["DEPLOYING", "DEPLOYED", "VERIFYING"] },
  { key: "released", label: "Released", statuses: ["COMPLETED"] },
  { key: "failed", label: "Failed", statuses: ["FAILED"] },
  { key: "rolled-back", label: "Rolled back", statuses: ["ROLLED_BACK"] },
  { key: "cancelled", label: "Cancelled", statuses: ["CANCELLED"] },
  { key: "archived", label: "Archived", statuses: ["ARCHIVED"], archived: true },
];

export function ReleasesTab({ projectId }: { projectId: string }) {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [tab, setTab] = useState("all");
  const [query, setQuery] = useState("");
  const [releaseType, setReleaseType] = useState("");
  const [page, setPage] = useState(0);
  /* This tab keeps its own state rather than the URL, as its filters do — the size follows them
     so the tab behaves one way throughout rather than two. */
  const [pageSize, setPageSize] = useState(25);
  const [creating, setCreating] = useState(false);

  /* TABS is a non-empty literal, but the compiler cannot know that from an index. */
  const chosen = TABS.find((entry) => entry.key === tab) ?? ALL_RELEASES;

  const releases = useQuery({
    queryKey: ["releases", projectId, tab, query, releaseType, page, pageSize],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), size: String(pageSize) });
      chosen.statuses?.forEach((status) => params.append("status", status));
      if (chosen.archived) {
        params.set("includeArchived", "true");
      }
      if (query.trim()) {
        params.set("query", query.trim());
      }
      if (releaseType) {
        params.set("releaseType", releaseType);
      }
      return api.get<PageResponse<ReleaseSummary>>(
        `/api/v1/projects/${projectId}/releases?${params}`,
      );
    },
  });

  /*
   * The bookings for the releases on this page, in one request rather than one per row. Keyed
   * by release id so the Slot column is a lookup, and enabled only once the page has arrived —
   * asking for the bookings of no releases is a round trip that can only answer "none".
   */
  const releaseIds = (releases.data?.content ?? []).map((release) => release.id);
  const bookings = useQuery({
    queryKey: ["release-slot-bookings", releaseIds],
    enabled: releaseIds.length > 0,
    queryFn: async () => {
      const found = await Promise.all(
        releaseIds.map((id) =>
          api.get<ReleaseSlotBookingView[]>(`/api/v1/releases/${id}/slot-bookings`),
        ),
      );
      const live: Record<string, ReleaseSlotBookingView> = {};
      found.flat().forEach((booking) => {
        if (booking.status === "PENDING" || booking.status === "APPROVED") {
          live[booking.releaseId] = booking;
        }
      });
      return live;
    },
  });

  const columns: Column<ReleaseSummary>[] = [
    {
      key: "version",
      header: "Version",
      render: (release) => (
        <div className="min-w-0">
          <span className="font-mono text-xs font-medium text-ink-900">{release.version}</span>
          {/* Whose version it is. Without this "3.9" beside "5.0" reads as two product
              releases, and the column becomes numbers meaning different things. */}
          {release.releaseScope !== "PROJECT" && release.moduleName && (
            <p className="truncate text-[11px] text-ink-500">{release.moduleName}</p>
          )}
        </div>
      ),
    },
    {
      key: "releaseName",
      header: "Release",
      render: (release) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-ink-900">{release.releaseName}</p>
          <p className="font-mono text-xs text-ink-400">{release.releaseKey}</p>
        </div>
      ),
    },
    {
      key: "releaseScope",
      header: "Release of",
      render: (release) =>
        release.releaseScope === "PROJECT" ? (
          <span className="text-ink-500">Whole product</span>
        ) : (
          <StatusBadge tone={release.releaseScope === "COMPONENT" ? "violet" : "blue"}>
            {release.releaseScope === "COMPONENT" ? "Component" : "Module"}
          </StatusBadge>
        ),
    },
    {
      key: "releaseType",
      header: "Type",
      render: (release) => (
        <StatusBadge tone={RELEASE_TYPE_TONE[release.releaseType]}>
          {RELEASE_TYPE_LABELS[release.releaseType]}
        </StatusBadge>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (release) => (
        <StatusBadge tone={RELEASE_STATUS_TONE[release.status]}>
          {RELEASE_STATUS_LABELS[release.status]}
        </StatusBadge>
      ),
    },
    {
      key: "owner",
      header: "Owner",
      render: (release) =>
        release.releaseManagerName ?? release.releaseOwnerName ?? (
          <span className="text-ink-400">Nobody assigned</span>
        ),
    },
    {
      key: "plannedReleaseAt",
      header: "Planned",
      render: (release) => (
        <span className="tnum text-xs">
          {release.plannedReleaseAt
            ? new Date(release.plannedReleaseAt).toLocaleDateString(undefined, {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : "—"}
        </span>
      ),
    },
    {
      key: "slot",
      header: "Slot",
      /* Which place on the release day this release holds, if it has asked for one. The
         question "what is this booking for" is answered here rather than only on the calendar,
         because this is the screen somebody is on when they wonder. */
      render: (release) => {
        const booking = bookings.data?.[release.id];
        if (!booking) {
          return <span className="text-ink-400">—</span>;
        }
        return (
          <span className="flex items-center gap-1.5">
            <StatusBadge tone={booking.status === "APPROVED" ? "green" : "amber"}>
              {booking.slot === "MORNING" ? "AM" : "PM"}
            </StatusBadge>
            {booking.emergency && (
              <span className="text-[10px] font-medium uppercase text-red-600">Emergency</span>
            )}
          </span>
        );
      },
    },
    {
      key: "environment",
      header: "Environment",
      render: (release) => release.targetEnvironment ?? <span className="text-ink-400">—</span>,
    },
    {
      key: "scope",
      header: "Scope",
      className: "w-36",
      render: (release) => (
        <ProgressBar
          value={release.scopeComplete}
          total={release.scopeTotal}
          tone={release.scopeComplete === release.scopeTotal ? "green" : "blue"}
          label="Complete"
        />
      ),
    },
    {
      key: "qa",
      header: "QA",
      className: "w-36",
      render: (release) => (
        <ProgressBar
          value={release.qaPassedCount}
          total={release.qaRequiredCount}
          tone={release.qaPassedCount === release.qaRequiredCount ? "green" : "amber"}
          label="Passed"
        />
      ),
    },
    {
      key: "readiness",
      header: "Readiness",
      render: (release) => (
        <StatusBadge tone={READINESS_TONE[release.readinessStatus]}>
          {READINESS_LABELS[release.readinessStatus]}
        </StatusBadge>
      ),
    },
    {
      key: "blockers",
      header: "Blockers",
      render: (release) =>
        release.openBlockerCount > 0 ? (
          <StatusBadge tone="red">{release.openBlockerCount}</StatusBadge>
        ) : (
          <span className="text-ink-400">—</span>
        ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap gap-3">
          <Field label="Search">
            <Input
              value={query}
              placeholder="Name, version or key"
              onChange={(event) => {
                setQuery(event.target.value);
                setPage(0);
              }}
            />
          </Field>
          <Field label="Type">
            <Select
              value={releaseType}
              onChange={(event) => {
                setReleaseType(event.target.value);
                setPage(0);
              }}
            >
              <option value="">All types</option>
              {Object.entries(RELEASE_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        {can("RELEASE_CREATE") && (
          <Button icon={<Plus className="h-4 w-4" />} onClick={() => setCreating(true)}>
            New release
          </Button>
        )}
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-ink-200">
        {TABS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            onClick={() => {
              setTab(entry.key);
              setPage(0);
            }}
            className={
              "-mb-px shrink-0 border-b-2 px-3 py-2 text-sm font-medium transition-colors "
              + (entry.key === tab
                ? "border-brand-600 text-brand-700"
                : "border-transparent text-ink-500 hover:border-ink-300 hover:text-ink-800")
            }
          >
            {entry.label}
          </button>
        ))}
      </div>

      <DataTable
        columns={columns}
        rows={releases.data?.content ?? []}
        rowKey={(release) => release.id}
        loading={releases.isPending}
        onRowClick={(release) => navigate(`/releases/${release.id}`)}
        page={page}
        pageSize={pageSize}
        totalElements={releases.data?.totalElements ?? 0}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        emptyTitle={query || releaseType ? "Nothing matches those filters" : "No releases yet"}
        emptyMessage={
          query || releaseType
            ? "Try a broader search, or clear the type filter."
            : "A release is a version with agreed scope. Create one as soon as you know it is coming — it does not have to be complete to be useful."
        }
        emptyAction={
          can("RELEASE_CREATE") && !query && !releaseType ? (
            <Button onClick={() => setCreating(true)}>Create the first release</Button>
          ) : undefined
        }
      />

      {/* §74, §75: how the releases above have actually gone. Below the list rather than above
          it, because somebody opening this tab is usually looking for a release, not a report. */}
      {can("RELEASE_REPORT_VIEW") && <ReleaseReportPanel projectId={projectId} />}

      <ReleaseFormDrawer
        projectId={projectId}
        open={creating}
        onClose={() => setCreating(false)}
        onSaved={(created) => created && navigate(`/releases/${created.id}`)}
      />
    </div>
  );
}
